import React, { useState, useEffect } from 'react';
import { Paper, Typography, FormControlLabel, Switch } from '@mui/material';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Global setting: auto-approve every new member registration (member portal).
 * Shown in Settings for staff with `members` (or `all`) permission.
 */
export default function MemberAutoApproveCard({ sx = {} }) {
  const { staff } = useAuth();
  const [autoApproveMembers, setAutoApproveMembers] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const perms = staff?.permissions || [];
  const canManageMembers = perms.includes('all') || perms.includes('members');

  useEffect(() => {
    if (!canManageMembers) {
      setSettingsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/app-settings');
        if (!cancelled && res.data?.data) {
          setAutoApproveMembers(!!res.data.data.auto_approve_members);
        }
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setSettingsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [staff?.permissions, canManageMembers]);

  const handleAutoApproveChange = async (e) => {
    const checked = e.target.checked;
    setAutoApproveMembers(checked);
    setSettingsSaving(true);
    try {
      await api.put('/app-settings', { auto_approve_members: checked });
    } catch {
      setAutoApproveMembers(!checked);
    } finally {
      setSettingsSaving(false);
    }
  };

  if (!canManageMembers) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        ...sx,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        New member registrations (all)
      </Typography>
      <FormControlLabel
        control={
          <Switch
            checked={autoApproveMembers}
            onChange={handleAutoApproveChange}
            disabled={settingsLoading || settingsSaving}
            color="primary"
          />
        }
        label="Automatically approve everyone who registers online"
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, maxWidth: 640 }}>
        Applies to all future sign-ups from the member portal. When on, new members are set to Active and their ID card
        is approved; you can still get staff notifications for each registration.
      </Typography>
    </Paper>
  );
}
